import React, { useState } from "react";
import {
  Card,
  Select,
  Table,
  Tabs,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  message,
  Statistic,
} from "antd";
import { DollarOutlined, CarOutlined, SearchOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { FetchDealers_api } from "../../Api/Dealers/dealer_api";
import {
  FetchDealerStock_api,
  MarkVehicleSold_api,
} from "../../Api/Stock/stock_api";
import { useAuthChecker } from "../../Context";
import dayjs from "dayjs";

const { Option } = Select;
const { TabPane } = Tabs;

export const DealerMonitor = () => {
  const { user } = useAuthChecker();
  const [selectedDealerId, setSelectedDealerId] = useState<number | null>(null);
  const [soldModalOpen, setSoldModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Pagination & Search
  const [activeTab, setActiveTab] = useState("1"); // 1=Available, 2=Sold
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");

  const { data: dealersData } = useQuery({
    queryKey: ["dealers", page, pageSize, searchText],
    queryFn: () => FetchDealers_api(page, pageSize, searchText),
  });

  const {
    data: stockData,
    refetch: refetchStock,
    isFetching,
  } = useQuery({
    queryKey: [
      "dealer_stock",
      selectedDealerId,
      page,
      pageSize,
      searchText,
      activeTab,
    ],
    queryFn: () =>
      selectedDealerId
        ? FetchDealerStock_api(
            selectedDealerId,
            page,
            pageSize,
            searchText,
            activeTab == "1"
              ? "available"
              : activeTab == "2"
                ? "sold"
                : activeTab == "3"
                  ? "returnd"
                  : "",
          )
        : Promise.resolve({ data: [], total: 0 }),
    enabled: !!selectedDealerId,
  });

  const handleSoldSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const payload = {
        vehicle_id: selectedVehicle.id,
        sold_remarks: values.sold_remarks,
        sold_date: values.sold_date
          ? values.sold_date.format("YYYY-MM-DD HH:mm:ss")
          : undefined,
        userId: user?.userId,
      };
      const res = await MarkVehicleSold_api(payload);
      if (res.isSold) {
        message.success(res.msg);
        setSoldModalOpen(false);
        refetchStock();
      } else {
        message.error(res.msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: "Vehicle", render: (r: any) => `${r.v_type} - ${r.v_modal}` },
    { title: "Engine No", dataIndex: "engine_number" },
    { title: "Chassis No", dataIndex: "chassis_number" },
    { title: "Color", dataIndex: "color_name" },
    {
      title: "Added Date",
      dataIndex: "added_date",
      render: (d: string) => d?.split(" ")[0],
    },
  ];

  const stockColumns = [
    ...columns,
    {
      title: "Mark as Sold",
      render: (r: any) => (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            setSelectedVehicle(r);
            setSoldModalOpen(true);
          }}
        >
          Mark Sold
        </Button>
      ),
    },
  ];

  const soldColumns = [
    ...columns,
    {
      title: "Sold Date",
      dataIndex: "sold_date",
      render: (d: string) => d?.split(" ")[0],
    },
    { title: "Remarks", dataIndex: "sold_remarks" },
  ];

  const returnedColumns = [
    ...columns,
    {
      title: "Returned Date",
      dataIndex: "return_date",
      render: (d: string) => d?.split(" ")[0],
    },
    { title: "Remark", dataIndex: "reaturn_reson" },
  ];

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dealer Monitor</h1>

      <Card className="mb-6 shadow-sm border-none">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <span className="font-medium">Select Dealer:</span>
            <Select
              className="w-64"
              placeholder="Select a dealer"
              onChange={(val) => {
                setSelectedDealerId(val);
                setPage(1);
                setSearchText("");
              }}
              showSearch
              optionFilterProp="children"
            >
              {dealersData?.dealerData?.map((d: any) => (
                <Option key={d.id} value={d.id}>
                  {d.name}
                </Option>
              ))}
            </Select>
          </div>
          {selectedDealerId && (
            <Input
              placeholder="Search dealer stock..."
              prefix={<SearchOutlined />}
              className="w-64"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(1);
              }}
            />
          )}
        </div>
      </Card>

      {selectedDealerId && (
        <div className="space-y-6">
          <Card className="shadow-sm border-none">
            <Tabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                setPage(1);
              }}
            >
              <TabPane tab="Current Stock" key="1">
                <Table
                  dataSource={stockData?.data || []}
                  columns={stockColumns}
                  rowKey="id"
                  loading={isFetching}
                  pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: stockData?.total || 0,
                    onChange: (p, ps) => {
                      setPage(p);
                      setPageSize(ps);
                    },
                    showSizeChanger: true,
                  }}
                  className="overflow-x-auto"
                />
              </TabPane>
              <TabPane tab="Sales History" key="2">
                <Table
                  dataSource={stockData?.data || []}
                  columns={soldColumns}
                  rowKey="id"
                  loading={isFetching}
                  pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: stockData?.total || 0,
                    onChange: (p, ps) => {
                      setPage(p);
                      setPageSize(ps);
                    },
                    showSizeChanger: true,
                  }}
                  className="overflow-x-auto"
                />
              </TabPane>
              <TabPane tab="Returned History" key="3">
                <Table
                  dataSource={stockData?.data || []}
                  columns={returnedColumns}
                  rowKey="id"
                  loading={isFetching}
                  pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: stockData?.total || 0,
                    onChange: (p, ps) => {
                      setPage(p);
                      setPageSize(ps);
                    },
                    showSizeChanger: true,
                  }}
                  className="overflow-x-auto"
                />
              </TabPane>
            </Tabs>
          </Card>
        </div>
      )}

      <Modal
        title="Mark Vehicle as Sold"
        open={soldModalOpen}
        onCancel={() => setSoldModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSoldSubmit}>
          <Form.Item label="Sold Date" name="sold_date" initialValue={dayjs()}>
            <DatePicker showTime className="w-full" />
          </Form.Item>
          <Form.Item
            label="Remarks"
            name="sold_remarks"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} placeholder="Customer name, price, etc." />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setSoldModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Confirm Sold
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
